import { OutageAffectedSystemEnum, RevolvingTransactionTypeEnum } from '.spec/self-care/tsTypes';
import { formatISO } from 'date-fns';
import { orderBy, take } from 'lodash';

import type { Amount } from '@skippay/core/@types/amount';
import { toFloat } from '@skippay/core/utils/amountUtils';
import { QueryFilter } from '@skippay/core/utils/apiUtils';
import { Paginated } from '@skippay/core/utils/queryUtils';
import { Maybe } from '@skippay/core/utils/types';

import { toIsoDate } from 'jarabot/utils';
import OutagesQuery from 'queries/OutagesQuery.graphql';

import { LIST_CAPS } from '../../constants';
import JarabotTransactionsQuery from '../../queries/JarabotTransactionsQuery.graphql';
import { OutageItem, TransactionItem } from '../../types';
import { DomainSource } from '../types';

interface TransactionRow {
	amount?: Maybe<Amount>;
	postingDate?: Maybe<string>;
	createdDate?: Maybe<string>;
	type?: Maybe<RevolvingTransactionTypeEnum>;
	isAuthorization?: Maybe<boolean>;
	billing?: Maybe<{ month?: Maybe<string> }>;
	additionalInfoCard?: Maybe<{ merchantName?: Maybe<string> }>;
	additionalInfoApplication?: Maybe<{ partnerName?: Maybe<string> }>;
}

const newestFirst = (rows?: Maybe<TransactionRow[]>): TransactionRow[] =>
	orderBy(rows, (transaction) => transaction.postingDate ?? transaction.createdDate ?? '', 'desc');

export const transactionsSource: DomainSource<{
	myContractTransactions?: Paginated<TransactionRow>;
}> = {
	domains: ['transactions'],
	query: JarabotTransactionsQuery,
	variables: { limit: LIST_CAPS.transactions },
	toModel: (data) => {
		const transactions: TransactionItem[] = take(
			newestFirst(data.myContractTransactions?.data),
			LIST_CAPS.transactions
		).map((transaction) => ({
			amount: transaction.amount ? toFloat(transaction.amount) : undefined,
			date: toIsoDate(transaction.postingDate ?? transaction.createdDate),
			type: transaction.type ?? undefined,
			billing: transaction.billing?.month ?? undefined,
			merchant:
				transaction.additionalInfoApplication?.partnerName ??
				transaction.additionalInfoCard?.merchantName ??
				undefined,
			isAuthorization: transaction.isAuthorization ?? undefined,
		}));
		return { transactions };
	},
};

interface OutageRow {
	affectedSystems?: Maybe<OutageAffectedSystemEnum[]>;
	startDatetime?: Maybe<string>;
	endDatetime?: Maybe<string>;
}

export const outagesSource: DomainSource<{ outages?: Paginated<OutageRow> }> = {
	domains: ['outages'],
	query: OutagesQuery,
	getVariables: () => ({
		filter: new QueryFilter()
			.prop('startDatetime')
			.lt(formatISO(new Date()))
			.prop('endDatetime')
			.gt(formatISO(new Date()))
			.toString(),
	}),
	toModel: (data) => {
		const rows = data.outages?.data ?? [];
		const outages: OutageItem[] = rows.map((outage) => ({
			system: outage.affectedSystems?.[0] ?? undefined,
			systems: outage.affectedSystems ?? undefined,
			startDate: toIsoDate(outage.startDatetime),
			endDate: toIsoDate(outage.endDatetime),
		}));
		return { outages };
	},
};
